/**
 * Full Basic calculator PDF + email metrics (same report as Download PDF).
 */
import { PDFExportManager } from '../PDFExportManager.js';

/** Last projection year with withdrawals — skips desktop phantom trailing row. */
export function getEndOfPeriodSuperBalance(chartData) {
  if (!chartData?.length) return 0;
  for (let i = chartData.length - 1; i >= 0; i--) {
    const row = chartData[i];
    const w = row.withdrawal ?? row.withdrawalFromSuper ?? 0;
    const income = row.totalIncome ?? 0;
    if (w > 0 || income > 0) {
      return Math.round(row.endingBalance ?? row.balance ?? 0);
    }
  }
  const last = chartData[chartData.length - 1];
  return Math.round(last?.endingBalance ?? last?.balance ?? 0);
}

export function getBasicMaxSustainableIncome(chartData, fallback = 0) {
  if (!chartData?.length) return Math.round(fallback);
  const row = chartData[0];
  if (row.totalIncome != null) return Math.round(row.totalIncome);
  return Math.round(
    (row.withdrawal || 0) + (row.agePension || row.pension || 0) + (row.otherIncome || 0) || fallback
  );
}

export function buildBasicEmailResults(chartData, extras = {}) {
  const finalBalance = getEndOfPeriodSuperBalance(chartData);
  const maxSustainableIncome = getBasicMaxSustainableIncome(chartData, extras.maxSustainableIncome);
  return {
    finalBalance,
    maxSustainableIncome,
    successRate: extras.successRate ?? (finalBalance >= 0 ? 100 : 0),
    periodLength: extras.periodLength,
    startYear: extras.startYear
  };
}

export function buildBasicEnhancedInputs(snapshotInputs, chartData) {
  const rows = chartData || [];
  return {
    ...snapshotInputs,
    maxSustainableIncome: getBasicMaxSustainableIncome(rows),
    successRate: getEndOfPeriodSuperBalance(rows) >= 0 ? 100 : 0,
    agePensionEstimate:
      rows.length > 0
        ? Math.round(rows.reduce((sum, y) => sum + (y.agePension || y.pension || 0), 0) / rows.length)
        : 0
  };
}

function svgToImage(svgElement, width, height) {
  return new Promise((resolve) => {
    try {
      const svgString = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0);
        const imageData = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve({ data: imageData, width, height });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    } catch {
      resolve(null);
    }
  });
}

/**
 * Capture Recharts containers used by desktop Basic calculator (same IDs as Download PDF).
 */
export async function captureBasicCalculatorChartsForPdf() {
  const chartImages = {};
  const maxWidth = 720;

  const captureChart = async (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return null;

    container.scrollIntoView({ behavior: 'instant', block: 'center' });
    await new Promise((r) => setTimeout(r, 400));

    const tooltips = container.querySelectorAll('.recharts-tooltip-wrapper, [role="tooltip"]');
    tooltips.forEach((t) => {
      t.style.display = 'none';
    });
    await new Promise((r) => setTimeout(r, 150));

    const svg = container.querySelector('svg');
    if (svg) {
      const svgRect = svg.getBoundingClientRect();
      const width = Math.min(Math.max(svgRect.width || 720, 400), maxWidth);
      const height = Math.max(svgRect.height || 280, 220);
      const result = await svgToImage(svg, width, height);
      tooltips.forEach((t) => {
        t.style.display = '';
      });
      if (result) return result;
    }

    if (typeof window !== 'undefined' && window.html2canvas) {
      try {
        const canvas = await window.html2canvas(container, {
          scale: 1,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
          allowTaint: true
        });
        tooltips.forEach((t) => {
          t.style.display = '';
        });
        return { data: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
      } catch {
        tooltips.forEach((t) => {
          t.style.display = '';
        });
      }
    }

    tooltips.forEach((t) => {
      t.style.display = '';
    });
    return null;
  };

  chartImages.balanceChart = await captureChart('basic-balance-chart');
  chartImages.incomeChart = await captureChart('basic-income-chart-msi');
  chartImages.incomeChartTarget = await captureChart('basic-income-chart-target');

  return chartImages;
}

/** Same report as Download PDF, including chart captures when the chart DOM is available. */
export async function generateBasicCalculatorPdfBase64(snapshotInputs, chartData, whatIfResults = {}) {
  const enhancedInputs = buildBasicEnhancedInputs(snapshotInputs, chartData);
  try {
    await new Promise((r) => setTimeout(r, 600));
    const chartImages = await captureBasicCalculatorChartsForPdf();
    return PDFExportManager.exportBasicCalculator(
      enhancedInputs,
      chartData,
      whatIfResults || {},
      chartImages,
      { returnBase64: true }
    );
  } catch (err) {
    console.error('[basicCalculatorPdfEmail] PDF generation failed:', err);
    return null;
  }
}
